/**
 * Forgot Password Page
 * ────────────────────
 * Step 1 of password reset: user enters their email.
 * Shows a confirmation message regardless of whether the email exists
 * (prevents email enumeration).
 *
 * Place at: src/pages/ForgotPasswordPage.jsx
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, Mail, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { authAPI } from '../api';
import { Button } from '../components/ui';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      // Backend always returns 200 to prevent enumeration,
      // but handle network errors gracefully
      const data = err?.response?.data;
      if (data?.error || data?.msg) {
        setError(data.error || data.msg);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -left-1/4 w-1/2 h-1/2 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -right-1/4 w-1/2 h-1/2 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Enumé</span>
          </button>
        </div>

        {/* Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 backdrop-blur-sm">
          {!submitted ? (
            <>
              {/* Back link */}
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Link>

              <h1 className="text-2xl font-bold text-white mb-2">
                Reset your password
              </h1>
              <p className="text-zinc-500 mb-8">
                Enter the email you used to create your account and we'll send you a reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      placeholder="you@example.com"
                      className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                      autoFocus
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  loading={loading}
                  className="w-full py-3"
                  icon={ArrowRight}
                  iconPosition="right"
                >
                  Send Reset Link
                </Button>
              </form>
            </>
          ) : (
            /* ── Success state ────────────────────────────────────── */
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>

              <h1 className="text-2xl font-bold text-white mb-3">
                Check your email
              </h1>
              <p className="text-zinc-400 mb-2">
                If an account exists for <span className="text-zinc-200 font-medium">{email}</span>, we've sent a link to reset your password.
              </p>
              <p className="text-zinc-500 text-sm mb-8">
                The link expires in 30 minutes. Check your spam folder if you don't see it.
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                  variant="ghost"
                  className="w-full"
                >
                  Try a different email
                </Button>

                <Link
                  to="/login"
                  className="block text-center text-sm text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Return to sign in
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-zinc-600 text-xs mt-6">
          Remember your password?{' '}
          <Link to="/login" className="text-zinc-500 hover:text-zinc-400 transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
